import { useState, useEffect, useRef } from "react";
import { Row, Col, Spinner, Container, Label, FormGroup, Input } from "reactstrap";
import { Button, Form } from "react-bootstrap";
import { Bar } from "react-chartjs-2";
import Chart from "chart.js";
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { BiImport } from "react-icons/bi";
import { BsCheckCircleFill } from "react-icons/bs";
import { useForm, Controller } from "react-hook-form";
import { useParams, useSearchParams } from "react-router-dom";
import { useReactToPrint } from "react-to-print";
import toast, { Toaster } from 'react-hot-toast'
import CryptoJS from 'crypto-js';
import { useSelector } from "react-redux";
import parse from 'html-react-parser';
import moment from 'moment';
import classNames from "classnames";

// Custom Component
import NavbarComponent from "../../components/layouts/Navbar";
import Footer from '../../components/layouts/Footer'
import ProgressPdfReportPage from "../pdfReport/ProgressPdfReportPage";
import SignatureModal from "../../components/modal/SignatureModal";

// Utility-Service
import { getToken, getUser } from "../../../utility/AuthService";

// Redux-Functions
import { selectLanguageUse } from '../../../redux/slices/language.Slice';

// API-Service
import { getProgressSubmittedDocumentServices } from "../../../services/uploadDocumentServices/getSubmittedDocumentServices";
import { getProgressAssessmentSummaryService } from "../../../services/progressReportServices/getProgressAssessmentSummaryService";
import { uploadApprovalInfoServices } from "../../../services/progressReportServices/uploadApprovalInfo";

const DashboardProgress = () => {
    const [searchParams] = useSearchParams();
    const download = searchParams.get('download');

    const componentRef = useRef();
    const AWS_Url = process.env.REACT_APP_AWSURL

    const [isLoading, setLoading] = useState(false);
    const [dimensionData, setDimensionData] = useState([]);
    const [profileData, setProfileData] = useState();
    const [readiness, setReadiness] = useState();
    const [ccptLevel, setCcptLevel] = useState();
    const [scorePercent, setScorePercent] = useState();
    const [actionPlan, setActionPlan] = useState([]);
    const [questionAns, setQuestionAns] = useState([]);
    const [pdfContent, setPdfContent] = useState(false);
    const [overallVal, setOverallVal] = useState();
    const [overallValLebel, setOverallValLebel] = useState();
    const [overallColor, setOverallColor] = useState();
    const [uploadedDocInfo, setUploadedDocInfo] = useState([])
    const [userSign, setUserSign] = useState();
    const [userSignUploaded, setUserSignUploaded] = useState(false);
    const [approveInfoUploaded, setApproveInfoUploaded] = useState(false);
    const [approveInfo, setApproveInfo] = useState();
    const [lastSubmission, setLastSubmission] = useState();
    const [signatureModalShow, setSignatureModalShow] = useState(false);

    const authToken = getToken()
    const { SId } = useParams()
    const user = getUser()
    const languageUse = useSelector(selectLanguageUse || user?.language)

    const { control, watch, reset, register, setError, clearErrors, handleSubmit, formState: { errors, isDirty, isValid } } = useForm({
        mode: "onBlur"
    });

    const decryptedSubId = CryptoJS.AES.decrypt(SId, 'mySecretKey').toString(CryptoJS.enc.Utf8);

    /**
        * @function handleSignatureModalShow
        * @param
        * @description  used to show signature modal
    */
    const handleSignatureModalShow = () => {
        setSignatureModalShow(!signatureModalShow);
    }

    /**
        * @function getSubmittedDocs
        * @param
        * @description get Submitted Documents
    */
    const getSubmittedDocs = async () => {
        let result = []
        try {
            setLoading(true)
            result = await getProgressSubmittedDocumentServices({
                authToken: authToken,
                SubmissionId: decryptedSubId
            });
            if (result?.data?.statusCode === 200) {
                setUploadedDocInfo(result?.data?.data)
            } else {
                setLoading(false)
                setUploadedDocInfo([])
                toast.error(result?.response?.data?.message || 'Something went wrong')
            }
        } catch (error) {
            setLoading(false)
            setUploadedDocInfo([])
            toast.error(result?.response?.data?.message || 'Something went wrong')
        }
    };

    useEffect(() => {
        getSubmittedDocs()
    }, []);

    /**
        * @function selectESGcolor
        * @param
        * @description used to select ESG color
    */
    const selectESGcolor = (value) => {
        if (value > 0 && value <= 20) {
            return '#E06666'
        } else if (value > 20 && value <= 40) {
            return '#FCE5CD'
        } else if (value > 40 && value <= 60) {
            return '#D9EAD3'
        } else if (value > 60 && value <= 80) {
            return '#B6D7A8'
        } else if (value > 80 && value <= 100) {
            return '#93C47D'
        }
    }

    useEffect(() => {
        if (dimensionData?.length > 0) {
            const labels = dimensionData?.length > 0 ? dimensionData?.map(item => languageUse === 'English' ? item.name : item.name_French) : [];
            labels.push(languageUse === 'English' ? 'Climate Action Readiness' : 'Préparation à l\'Action Climatique');
            setOverallValLebel(labels)

            const totalValues = dimensionData?.length > 0 ? dimensionData?.map(item => (item.total)) : []
            let totalValuesSum = 0;
            totalValues.map(x => (totalValuesSum += x));

            const correctValues = dimensionData?.length > 0 ? dimensionData?.map(item => (item.correct)) : []
            let correctValuesSum = 0;
            correctValues.map(x => (correctValuesSum += x));

            const overallValue = ((correctValuesSum / totalValuesSum) * 100).toFixed(2)
            const ESGcolor = selectESGcolor(overallValue)
            const allColor = dimensionData?.length > 0 && dimensionData?.map(item => selectESGcolor(((item?.correct / item?.total) * 100).toFixed(2)))
            allColor.push(ESGcolor)
            setOverallColor(allColor)

            const chartData = dimensionData?.length > 0 && dimensionData?.map(item => ((item.correct / item.total) * 100).toFixed(2));
            chartData.push(overallValue);
            setOverallVal(chartData)
        }
    }, [dimensionData, languageUse]);

    const data = {
        labels: overallValLebel,
        datasets: [
            {
                label: "Score of Category",
                data: overallVal?.map(val => {
                    const numVal = Number(val);
                    if (isNaN(numVal)) return 0;
                    // Ensure value doesn't exceed 100%
                    return Math.min(numVal, 100);
                }) || [],
                backgroundColor: overallColor,
                borderColor: overallColor,
                borderWidth: 1
            }
        ]
    };

    const options = {
        responsive: true,
        plugins: {
            legend: {
                display: false,
            },
            title: {
                display: true,
                text: "Score of Category"
            },
            datalabels: {
                anchor: 'end',
                align: 'top',
                color: "black",
                display: function (context) {
                    return context.dataset.data[context.dataIndex] > 0;
                },
                font: {
                    weight: "bold",
                },
                formatter: (value) => {
                    return `${value}%`
                }
            },
        },
        scales: {
            x: {
                max: 100,
                ticks: {
                    callback: (value) => {
                        return `${value}%`
                    }
                },
                grid: {
                    display: false,

                },
                border: {
                    display: false,
                },
                position: 'bottom'
            },
            y: {
                max: 100,
                ticks: {
                    display: false
                },
                grid: {
                    display: false
                },
                border: {
                    display: true
                }
            }
        },
        maintainAspectRatio: true
    };

    const getDatasetData = () => {
        if (!dimensionData?.length) return [];
        const scores = dimensionData.map(item => {
            const score = ((item?.correct / item?.total) * 100).toFixed(2);
            const numScore = Number(score);
            return isNaN(numScore) ? 0 : Math.min(numScore, 100);
        });
        return [...scores, Math.min(Number(overallValue) || 0, 100)];
    };

    const horizontalData = {
        labels: dimensionData?.length > 0 ? [...dimensionData?.map(item => languageUse === 'English' ? item?.name || '' : item?.name_French || ''), languageUse === 'English' ? "Climate Action Readiness" : "Préparation à l'Action Climatique"] : [],
        datasets: [
            {
                label: languageUse === 'English' ? "Your Score" : "Votre Score",
                data: getDatasetData(),
                backgroundColor: allColor,
                borderColor: allColor,
                borderWidth: 1
            },
            {
                label: languageUse === 'English' ? "Peer Score" : "Score des Pairs",
                data: dimensionData?.length > 0 ? dimensionData.map(item => {
                    if (item?.industryScore) {
                        const score = ((item?.industryScore / item?.total) * 100).toFixed(2);
                        const numScore = Number(score);
                        return isNaN(numScore) ? 0 : Math.min(numScore, 100);
                    } else {
                        return 0;
                    }
                }) : [],
                backgroundColor: '#D3D3D3',
                borderColor: '#D3D3D3',
                borderWidth: 1
            }
        ]
    };

    const horoizontalOptions = {
        responsive: true,
        indexAxis: 'y',
        plugins: {
            legend: {
                display: true,
                labels: {
                    usePointStyle: true,
                    boxWidth: 20,
                    padding: 50
                },
                position: 'bottom',
                align: 'center'
            },
            title: {
                display: true,
                text: "Score of Category"
            },
            datalabels: {
                display: false
            }
        },
        scales: {
            x: {
                max: 100,
                ticks: {
                    callback: (value) => {
                        return `${value}%`
                    }
                },
                grid: {
                    display: false,
                },
                border: {
                    display: false,
                },
                position: 'bottom'
            },
            y: {
                grid: {
                    display: false,
                },
                border: {
                    display: false,
                }
            }
        },
        maintainAspectRatio: true,
    };

    /**
        * @function getAssesmentSummary
        * @param
        * @description get Assesment Summary List
    */
    useEffect(() => {
        let isSubscribed = true;

        const fetchData = async () => {
            try {
                setLoading(true);
                const result = await getProgressAssessmentSummaryService({
                    authToken,
                    SubmissionId: decryptedSubId
                });

                // Only update state if component is still mounted
                if (!isSubscribed) return;

                // Validate API response structure
                if (!result?.data?.data) {
                    throw new Error('Invalid API response structure');
                }

                const { data } = result.data;

                // Validate and set profile data
                setProfileData(data?.profile || null);
                setReadiness(data?.readinessLeval || null);
                setCcptLevel(data?.ccptLevel || null);
                setActionPlan(Array.isArray(data?.actionPlans) ? data?.actionPlans : []);

                // Validate dimension scoring data
                const validDimensionData = Array.isArray(data?.dimensionScoring) ? data?.dimensionScoring.map(item => ({ ...item, correct: Number(item?.correct) || 0, total: Number(item?.total) || 1, name: item?.name || 'Unnamed Dimension', name_French: item?.name_French || 'Unnamed Dimension' })) : [];
                setDimensionData(validDimensionData);

                setScorePercent(Number(data?.scorePercent) || 0);
                setQuestionAns(Array.isArray(data?.questionAnswers) ? data?.questionAnswers : []);
                setUserSign(data?.approvedBySign || null);
                setApproveInfo(data?.approvedBy || null);
                setLastSubmission(data?.lastSubmissionDate || null);

            } catch (error) {
                // Only update error state if component is still mounted
                if (!isSubscribed) return;

                console.error('Dashboard Progress Error:', {
                    message: error?.message,
                    response: error?.response?.data,
                    submissionId: decryptedSubId
                });

                toast.error(
                    error?.response?.data?.message || 
                    'Unable to load dashboard data. Please try again later.'
                );
            } finally {
                // Only update loading state if component is still mounted
                if (!isSubscribed) return;
                setLoading(false);
            }
        };

        fetchData();

        // Cleanup function to handle component unmounting
        return () => {
            isSubscribed = false;
        };
    }, [userSignUploaded, approveInfoUploaded, authToken, decryptedSubId]);

    /**
        * @function handleApprovalInfo
        * @param ()
        * @description upload aprroval information
    */
    const handleApprovalInfo = async (data) => {
        try {
            setLoading(true);
            const result = await uploadApprovalInfoServices({
                authToken,
                submissionId: decryptedSubId,
                name: data?.name,
                desigantion: data?.designation,
                date: data?.date

            });
            if (result?.data?.statusCode === 200) {
                setLoading(false);
                setApproveInfoUploaded(!approveInfoUploaded)
                toast.success('Uploaded')
            }
        } catch (error) {
            setLoading(false);
            toast.error('Something went wrong.')
        }
    }

    /**
        * @function onSubmit
        * @param (data)
        * @description to submit
    */
    const onSubmit = data => {
        handleApprovalInfo(data)
    }

    useEffect(() => {
        const defaultValues = {};
        defaultValues.name = approveInfo?.name
        defaultValues.designation = approveInfo?.desigantion
        defaultValues.date = approveInfo?.date
        reset({ ...defaultValues })
    }, [approveInfoUploaded, approveInfo])

    /**
        * @function afterPrint
        * @param 
        * @description set pdf content false after the print
    */
    const afterPrint = () => {
        setPdfContent(false)
    }

    /**
        * @function pdfContenFunc
        * @param
        * @description handle pdf content
    */
    const pdfContenFunc = useReactToPrint({
        pageStyle: `@media print {
                  @page {
                    size: A4 !important;
                    margin: 0 !important;
                  }
                  @page :footer {
                    display: none !important;
                  }

                  @page :header {
                    display: none !important;
                  }

                  html, body {
                    height: 100%; 
                    margin: 0 !important;
                    padding: 0 !important;
                    -webkit-print-color-adjust: exact !important;
                    print-color-adjust: exact !important;
                  }
                  
                  .report-container {
                    width: 210mm !important;
                    margin: 0 auto !important;
                  }
                }`,
        content: () => componentRef.current,
        documentTitle: `SME_ESG_Report_${profileData?.company}_${new Date().toISOString().split('T')[0]}`,
        onAfterPrint: afterPrint
    })

    /**
        * @function pdfDownloadFunc
        * @param
        * @description handle pdf download
    */
    const pdfDownloadFunc = () => {
        setPdfContent(true)
        const timer = setTimeout(() => {
            pdfContenFunc()
        }, 1000);

        return () => clearTimeout(timer);
    };

    useEffect(() => {
        if (download) {
            pdfDownloadFunc()
        }
    }, [readiness])

    const getMaturityLevelText = (level, frenchLevel) => {
        return languageUse === 'English' ? level : frenchLevel;
    };

    const renderMobileCheckmark = (level, frenchLevel) => {
        const shouldShowCheckmark = 
            (languageUse === 'English' && readiness?.leval === level) ||
            (languageUse !== 'English' && readiness?.leval_French === frenchLevel);
        return shouldShowCheckmark ? <BsCheckCircleFill /> : '';
    };

    const handlePrint = useReactToPrint({
        content: () => componentRef.current,
        documentTitle: `SME_ESG_Report_${profileData?.company}_${new Date().toISOString().split('T')[0]}`,
        onBeforeGetContent: () => {
            setPdfContent(true);
            return new Promise((resolve) => {
                setTimeout(() => {
                    resolve();
                }, 500);
            });
        },
        onAfterPrint: () => {
            setPdfContent(false);
            toast.success(languageUse === 'English' ? 'Report downloaded successfully' : 'Laporan berjaya dimuat turun');
        }
    });

    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        const shouldGenerateReport = searchParams.get('generateReport') === 'true';
        
        if (shouldGenerateReport && componentRef.current) {
            // Automatically trigger report generation
            handlePrint();
            
            // Clear the URL parameter
            const newUrl = window.location.pathname;
            window.history.replaceState({}, '', newUrl);
        }
    }, [location.search, componentRef.current]);

    const pdfData = {
        labels: overallValLebel,
        datasets: [
            {
                label: languageUse === 'English' ? "Score of Category" : "Score des catégories",
                data: overallVal?.map(val => {
                    const numVal = Number(val);
                    return isNaN(numVal) ? 0 : Math.min(numVal, 100);
                }) || [],
                backgroundColor: overallColor,
                borderColor: overallColor,
                borderWidth: 1
            }
        ]
    };

    const pdfHorizontalData = {
        labels: dimensionData?.length > 0 ? [...dimensionData?.map(item => languageUse === 'English' ? item?.name || '' : item?.name_French || ''), languageUse === 'English' ? "Climate Action Readiness" : "Préparation à l'Action Climatique"] : [],
        datasets: [
            {
                label: languageUse === 'English' ? "Your Score" : "Votre Score",
                data: dimensionData?.length > 0 ? dimensionData.map(item => {
                    const score = ((item?.correct / item?.total) * 100).toFixed(2);
                    return Math.min(Number(score) || 0, 100);
                }) : [],
                backgroundColor: allColor,
                borderColor: allColor,
                borderWidth: 1
            },
            {
                label: languageUse === 'English' ? "Peer Score" : "Score des Pairs",
                data: dimensionData?.length > 0 ? dimensionData.map(item => {
                    if (item?.industryScore) {
                        const score = ((item?.industryScore / item?.total) * 100).toFixed(2);
                        return Math.min(Number(score) || 0, 100);
                    }
                    return 0;
                }) : [],
                backgroundColor: '#D3D3D3',
                borderColor: '#D3D3D3',
                borderWidth: 1
            }
        ]
    };

    const pdfOptions = {
        ...options,
        scales: {
            ...options.scales,
            y: {
                ...options.scales.y,
                max: 100
            }
        }
    };

    const pdfHorizontalOptions = {
        ...horoizontalOptions,
        scales: {
            ...horoizontalOptions.scales,
            x: {
                ...horoizontalOptions.scales.x,
                max: 100
            }
        }
    };

    return (
        <>
            <div>
                <Toaster />
            </div>
            <NavbarComponent />

            {isLoading ? <div className="d-flex justify-content-center align-items-center" style={{ height: '70vh' }}>
                <Spinner animation="grow" />
            </div> : <>
                <Container className="pdfSize">
                    <div className="d-flex justify-content-between">
                        <h2 className="mb-4">{languageUse === 'English' ? 'SME ESG Climate Action Maturity Report' : 'Laporan Kematangan Tindakan Iklim SME ESG'}</h2>
                        <div className="d-none d-lg-block">
                            <Button className="float-end authButton login-btn-min-width mb-4" variant='outline-primary' onClick={pdfDownloadFunc}>
                                {isLoading ? <Spinner variant='dark' size='sm' /> : <><BiImport style={{ fontSize: "20px", marginBottom: '3px' }}></BiImport> {languageUse === 'English' ? '  Report' : '  Rapport'}</>}
                            </Button>
                        </div>
                    </div>
                    <div className="profile">
                        <Row className="profile p-2 mb-0">
                            <Col className="" md="6" sm="12">
                                <p className="mb-0">{languageUse === 'English' ? 'Current Report Dated' : 'Rapport Actuel Daté du'} : <span className="bold">{moment(profileData?.updatedAt).format("DD-MM-YYYY")}</span></p>
                                <p className="mb-0">{languageUse === 'English' ? 'Company' : 'Entreprise'} : <span className="bold">{profileData?.company}</span></p>
                                <p className="mb-0">{languageUse === 'English' ? 'Company Registration Number' : 'Numéro d\'Enregistrement de l\'Entreprise'} : <span className="fw-bold">{profileData?.companyReg}</span></p>
                                <p className="mb-0">{languageUse === 'English' ? 'Username' : 'Nom d\'utilisateur'} : <span className="bold">{profileData?.username}</span></p>
                                <p className="mb-0">
                                    {languageUse === 'English' ? 'State' : 'État'} : <span className="fw-bold">{profileData?.state}</span>
                                </p>
                            </Col>
                            <Col className="" md="6" sm="12">
                                <p className="mb-0">{languageUse === 'English' ? 'Last Submission Date' : 'Date de Dernière Soumission'} :{" "}<span className="bold">{lastSubmission ? moment(lastSubmission).format("DD-MM-YYYY") : 'Aucune Soumission Précédente'}</span></p>
                                <p className="mb-0">{languageUse === 'English' ? 'Industry' : 'Industrie'} :{" "}<span className="bold">{languageUse === 'English' ? profileData?.industry?.name : profileData?.industry?.name_French}</span></p>
                                <p className="mb-0">
                                    Email : <span className="bold">{profileData?.email}</span>
                                </p>
                                <p className="mb-0">
                                    {languageUse === 'English' ? 'Country' : 'Pays'} : <span className="fw-bold">{profileData?.country}</span>
                                </p>
                                <p className="mb-0">
                                    {languageUse === 'English' ? 'City' : 'Ville'} : <span className="fw-bold">{profileData?.city}</span>
                                </p>
                            </Col>
                        </Row>
                    </div>

                    <Row className="p-0 my-2 d-none d-lg-block">
                        <table className="table table-bordered border-0">
                            <tbody>
                                <tr className="border-0">
                                    <td rowSpan={2} className="ps-3 text-center fw-bold border">{languageUse === 'English' ? "Your Climate Action Maturity Level:" : "Votre Niveau de Maturité d'Action Climatique :"}</td>
                                    <td className="ps-3 border-0 text-center fw-bold text-dark bg-Laggard">{languageUse === 'English' ? 'Laggard' : 'Laggard'}</td>

                                    <td className="ps-3 border-0 text-center fw-bold text-dark bg-Beginner">{languageUse === 'English' ? 'Beginner' : 'Débutant'}</td>

                                    <td className="ps-3 border-0 text-center fw-bold text-dark bg-Intermediate">{languageUse === 'English' ? 'Intermediate' : 'Intermédiaire'}</td>

                                    <td className="ps-3 border-0 text-center fw-bold text-white bg-Advanced">{languageUse === 'English' ? 'Advanced' : 'Avancé'}</td>

                                    <td className="ps-3 border-0 text-center fw-bold text-white bg-Leader">{languageUse === 'English' ? 'Leader' : 'Leader'} </td>
                                </tr>
                                <tr className="border-0">
                                    <td className={classNames("ps-3 border-0 text-center fw-bold", { 'bg-Laggard': readiness?.leval === 'Laggard' || readiness?.leval_French === 'Laggard' })}>
                                        {renderMobileCheckmark('Laggard', 'Laggard')}
                                    </td>

                                    <td className={classNames("ps-3 border-0 text-center fw-bold", { 'bg-Beginner': readiness?.leval === 'Beginner' || readiness?.leval_French === 'Débutant', })}>
                                        {renderMobileCheckmark('Beginner', 'Débutant')}
                                    </td>

                                    <td className={classNames("ps-3 border-0 text-center fw-bold", { 'bg-Intermediate': readiness?.leval === 'Intermediate' || readiness?.leval_French === 'Intermédiaire', })}>
                                        {renderMobileCheckmark('Intermediate', 'Intermédiaire')}
                                    </td>

                                    <td className={classNames("ps-3 border-0 text-center fw-bold", { 'bg-Advanced': readiness?.leval === 'Advanced' || readiness?.leval_French === 'Avancé', })}>
                                        {renderMobileCheckmark('Advanced', 'Avancé')}
                                    </td>

                                    <td className={classNames("ps-3 border-0 text-center fw-bold", { 'bg-Leader': readiness?.leval === 'Leader' || readiness?.leval_French === 'Leader', })}>
                                        {renderMobileCheckmark('Leader', 'Leader')}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </Row>

                    {/* For Small device */}
                    <Row className="p-0 my-2 d-block d-lg-none">
                        <h5 className="fw-bold">{languageUse === 'English' ? "Your Climate Action Maturity Level:" : "Votre Niveau de Maturité d'Action Climatique :"}</h5>
                        <table className="table table-bordered">
                            <tbody>
                                <tr>
                                    <td className="ps-3 fw-bold text-dark" style={{ backgroundColor: '#E06666' }}>
                                        {getMaturityLevelText('Laggard', 'Laggard')}
                                    </td>
                                    <td className="ps-3 fw-bold text-center">
                                        {renderMobileCheckmark('Laggard', 'Laggard')}
                                    </td>
                                </tr>

                                <tr>
                                    <td className="ps-3 fw-bold text-dark" style={{ backgroundColor: '#FCE5CD' }}>
                                        {getMaturityLevelText('Beginner', 'Débutant')}
                                    </td>
                                    <td className="ps-3 fw-bold text-center">
                                        {renderMobileCheckmark('Beginner', 'Débutant')}
                                    </td>
                                </tr>

                                <tr>
                                    <td className="ps-3 fw-bold text-dark" style={{ backgroundColor: '#D9EAD3' }}>
                                        {getMaturityLevelText('Intermediate', 'Intermédiaire')}
                                    </td>
                                    <td className="ps-3 fw-bold text-center">
                                        {renderMobileCheckmark('Intermediate', 'Intermédiaire')}
                                    </td>
                                </tr>

                                <tr>
                                    <td className="ps-3 fw-bold text-white" style={{ backgroundColor: '#B6D7A8' }}>
                                        {getMaturityLevelText('Advanced', 'Avancé')}
                                    </td>
                                    <td className="ps-3 fw-bold text-center border-0">
                                        {renderMobileCheckmark('Advanced', 'Avancé')}
                                    </td>
                                </tr>

                                <tr>
                                    <td className="ps-3 fw-bold text-white" style={{ backgroundColor: '#93C47D' }}>
                                        {getMaturityLevelText('Leader', 'Leader')}
                                    </td>
                                    <td className="ps-3 fw-bold text-center border-0">
                                        {renderMobileCheckmark('Leader', 'Leader')}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </Row>

                    <Row className="mt-2 pt-2" style={{ backgroundColor: '#F4FBFE', borderRadius: '5px', border: '1px solid #009EDB' }}>
                        <h5 className="fw-bold mb-2">{profileData?.company}</h5>
                        <span className="textColor mb-2">{languageUse === 'English' ? readiness?.leval : readiness?.leval_French} :</span>

                        <p className="">{languageUse === 'English' ? readiness?.description : readiness?.description_French}</p>
                    </Row>

                    <Row className="justify-content-between align-items-center mt-2">
                        <Col
                            className="history-text-color px-0 mb-4 mb-md-0"
                            md="6"
                            sm="12"
                        >
                            <h4 className="ps-3 pt-2 mb-4">{languageUse === 'English' ? "Score of Dimensions" : "Skor Dimensi"}</h4>

                            <div className="mt-5 d-flex justify-content-center align-items-center">
                                <Bar data={data} options={options} height={questionAns?.length > 0 && questionAns[2]?.["Business Strategy Integration"]?.length > 0 && questionAns[2]?.["Business Strategy Integration"][0]?.yesAnswers?.length > 8 ? 270 : 150}></Bar>
                            </div>
                        </Col>

                        <Col
                            className="history-text-color px-2 my-3"
                            md="6"
                            sm="12"
                        >
                            <div className="">
                                <table className="table table-bordered mb-0">
                                    <thead className="table-head">
                                        <tr className="history-text-color">
                                            <th className="ps-3 border-end-0" scope="col">{languageUse === 'English' ? "GHG emissions" : "Pelepasan GHG"}</th>
                                            <th className="ps-3 border-start-0" scope="col"></th>
                                        </tr>
                                    </thead>

                                    <tbody>
                                        <tr>
                                            <td className="ps-3">{languageUse === 'English' ? `Scope 1` : "Portée 1"}</td>
                                            {questionAns?.length > 0 && questionAns[1]?.["GHG emissions reduction"]?.length > 0 && questionAns[1]?.["GHG emissions reduction"][0]?.yesAnswers?.length > 0 ? questionAns[1]?.["GHG emissions reduction"][0]?.yesAnswers?.map((item, index) => (
                                                <td key={index} className="ps-3">{`${item} MTCO2e`}</td>
                                            )) : <td className="ps-3">N/A</td>}
                                        </tr>
                                        <tr>
                                            <td className="ps-3">{languageUse === 'English' ? `Scope 2` : "Portée 2"}</td>
                                            {questionAns?.length > 0 && questionAns[1]?.["GHG emissions reduction"]?.length > 0 && questionAns[1]?.["GHG emissions reduction"][1]?.yesAnswers?.length > 0 ? questionAns[1]?.["GHG emissions reduction"][1]?.yesAnswers?.map((item, index) => (
                                                <td key={index} className="ps-3">{`${item} MTCO2e`}</td>
                                            )) : <td className="ps-3">N/A</td>}
                                        </tr>
                                        <tr>
                                            <td className="ps-3">{languageUse === 'English' ? `GHG emission reduction target` : "Objectif de Réduction des Émissions de GES"}</td>
                                            {questionAns?.length > 0 && questionAns[1]?.["GHG emissions reduction"]?.length > 0 && questionAns[1]?.["GHG emissions reduction"][2]?.answer ? <td className="ps-3 d-flex flex-column">{questionAns[1]?.["GHG emissions reduction"][2]?.answer === "Yes" ? `${questionAns[1]?.["GHG emissions reduction"][2]?.answer}, \n ${questionAns[1]?.["GHG emissions reduction"][2]?.yesAnswers} MTCO2e` : questionAns[1]?.["GHG emissions reduction"][2]?.answer}</td> : <td className="ps-3">N/A</td>}
                                        </tr>

                                    </tbody>
                                </table>
                            </div>
                            <div className="">
                                <table className="table table-bordered mb-0">
                                    <thead className="table-head">
                                        <tr className="history-text-color">
                                            <th className="ps-3" scope="col">{languageUse === 'English' ? "Certification" : "Certification"}</th>
                                        </tr>
                                    </thead>

                                    <tbody>
                                        {questionAns?.length > 0 && questionAns[2]?.["Business Strategy Integration"]?.length > 0 && questionAns[2]?.["Business Strategy Integration"][0]?.yesAnswers?.length > 0 ? questionAns[2]?.["Business Strategy Integration"][0]?.yesAnswers?.map((item, index) => (
                                            <tr key={index}>
                                                <td className="ps-3">{item}</td>
                                            </tr>
                                        )) : <tr>
                                            <td className="ps-3">N/A</td>
                                        </tr>}
                                    </tbody>
                                </table>
                            </div>
                        </Col>
                    </Row>

                    <Row className="border mt-5" >
                        <h4 className="pt-3">{languageUse === 'English' ? "Peer Comparison Bar" : "Carta Perbandingan Rakan Industri"}</h4>
                        <div className="mt-3">
                            <Bar style={{ marginRight: '5rem', paddingRight: '5rem', marginBottom: '5px' }} data={horizontalData} options={horoizontalOptions} height={100}></Bar>
                        </div>
                    </Row>

                    <Row className="my-3">
                        <h4 className="pt-2 my-3 fw-bold text-center">{languageUse === 'English' ? "CLIMATE TRANSITION ACTION PLAN" : "PLAN D'ACTION DE TRANSITION CLIMATIQUE"}</h4>
                        <p>{languageUse === 'English' ? `This is an overview of ${profileData?.company} climate transition action plan. The action plan is developed according to 3 focus areas: climate governance, emissions reduction and business strategy integration. The management of ${profileData?.company} hereby commits to implement the following action plan within 18 months from the date of signature.` : `Ceci est un aperçu du plan d'action de transition climatique de ${profileData?.company}. Le plan d'action est développé selon 3 domaines d'intervention : la gouvernance climatique, la réduction des émissions et l'intégration de la stratégie commerciale. La direction de ${profileData?.company} s'engage par la présente à mettre en œuvre le plan d'action suivant dans les 18 mois suivant la date de signature.`}</p>
                    </Row>

                    {actionPlan?.length > 0 && actionPlan?.map((itemAction, indexAction) => {
                        return <div key={indexAction}>
                            <Row>
                                <div className={classNames({ "bg-Progress": scorePercent <= 50, "bg-Leader": scorePercent > 50 })}>
                                    <h4 className="pt-2 mb-3 fw-bold text-white">
                                        {languageUse === 'English' ? itemAction?.proGress_Dimension?.name ? parse(itemAction?.proGress_Dimension?.name) : '--' : itemAction?.proGress_Dimension?.name_French ? parse(itemAction?.proGress_Dimension?.name_French) : '--'}
                                    </h4>
                                    <p className="text-white">
                                        {languageUse === 'English' ? itemAction?.actionPlan ? parse(itemAction?.actionPlan) : '--' : itemAction?.actionPlan_French ? parse(itemAction?.actionPlan_French) : '--'}
                                    </p>

                                </div>

                                <div className="my-2 px-0 table-responsive">
                                    <table className="table table-bordered">
                                        <thead className={classNames('table-head', { "bg-Progress": scorePercent <= 50, "bg-Leader": scorePercent > 50 })}>
                                            <tr className="text-white">
                                                <th className="ps-3" scope="col">{languageUse === 'English' ? "Action Plan Objective" : "Objectif du Plan d'Action"}</th>
                                                <th className="ps-3" scope="col">{languageUse === 'English' ? "Key Actions" : "Actions Clés"}</th>
                                                <th className="ps-3" scope="col">{languageUse === 'English' ? "Outcomes" : "Résultats"}</th>
                                                <th className="ps-3" scope="col">{languageUse === 'English' ? "Timeline" : "Calendrier"}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr>
                                                <td className="ps-3">
                                                    {languageUse === 'English' ? itemAction?.objective ? parse(itemAction?.objective) : '--' : itemAction?.resources_French ? parse(itemAction?.resources_French) : '--'}
                                                </td>
                                                <td>
                                                    {languageUse === 'English' ? itemAction?.process ? parse(itemAction?.process) : '--' : itemAction?.process_French ? parse(itemAction?.process_French) : '--'}
                                                </td>
                                                <td>
                                                    {languageUse === 'English' ? itemAction?.output ? parse(itemAction?.output) : '--' : itemAction?.output_French ? parse(itemAction?.output_French) : '--'}
                                                </td>
                                                <td>
                                                    {languageUse === 'English' ? itemAction?.timeline ? parse(itemAction?.timeline) : '--' : itemAction?.timeline_French ? parse(itemAction?.timeline_French) : '--'}
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </Row>
                        </div>
                    })}

                    <Row className="d-flex align-items-end">
                        <Col className="d-flex flex-column" md="8" sm="12">
                            <p>{languageUse === 'English' ? "This Report Has Been Checked and Approved By :" : "Ce Rapport a été Vérifié et Approuvé Par :"}</p>
                            <Form onSubmit={handleSubmit(onSubmit)}>
                                <div className="mb-1 d-flex w-50">
                                    <Label className='form-label mb-0 d-flex align-items-center' for='name'>
                                        {languageUse === 'English' ? "Name" : "Nom"}&nbsp;:
                                    </Label>
                                    <Form.Control
                                        className="ms-2 border-0 border-bottom rounded-0"
                                        type="text"
                                        placeholder=""
                                        {...register("name", {
                                            required: languageUse === 'English' ? "Name Is Required" : "Nom Diperlukan"
                                        })}
                                    />
                                </div>
                                {errors?.name?.message && <span className='text-danger text-capitalize'>{errors?.name?.message}</span>}

                                <div className="mb-1 d-flex w-50">
                                    <Label className='form-label mb-0 d-flex align-items-center' for='designation'>
                                        {languageUse === 'English' ? "Designation" : "Fonction"}&nbsp;:
                                    </Label>
                                    <Form.Control
                                        className="ms-2 border-0 border-bottom rounded-0"
                                        type="text"
                                        placeholder=""
                                        {...register("designation", {
                                            required: languageUse === 'English' ? "Designation Is Required" : "La fonction est requise"
                                        })}
                                    />
                                </div>
                                {errors?.designation?.message && <span className='text-danger text-capitalize'>{errors?.designation?.message}</span>}

                                <div className="mb-1 ">
                                    <FormGroup className="d-flex w-50">
                                        <Label className="d-flex align-items-center mb-0" for="exampleDate">{languageUse === 'English' ? "Date" : "Date"}&nbsp;:</Label>

                                        <Controller
                                            name="date"
                                            control={control}
                                            render={({ field }) => (
                                                <Input
                                                    className="border-0 border-bottom rounded-0"
                                                    type="date"
                                                    id="date"
                                                    placeholder=""
                                                    max={new Date().toISOString().split("T")[0]}
                                                    {...field}
                                                />
                                            )}
                                            rules={{
                                                required: languageUse === 'English' ? 'Date is required' : 'La date est requise'
                                            }}
                                        />
                                    </FormGroup>
                                </div>
                                {errors?.date?.message && <span className='text-danger text-capitalize'>{errors?.date?.message}</span>}


                                <div className=''>
                                    <Button className="mt-2 authButton login-btn-min-width" variant='outline-primary' color="primary" type="submit">
                                        {isLoading ? <Spinner variant='dark' size='sm' /> : languageUse === 'English' ? 'Save' : 'Simpan'}
                                    </Button>
                                </div>
                            </Form>
                        </Col>

                        <Col className="d-flex flex-column justify-content-end pb-5" md="4" sm="12">
                            <div role="button" onClick={() => handleSignatureModalShow()} >
                                {userSign && <div className="">
                                    <img src={`${AWS_Url}${userSign}`} width={120} alt="sign" />
                                </div>}
                                <hr className="w-100 mb-0"></hr>
                                <p>{languageUse === 'English' ? 'Signature' : 'Signature'}</p>
                            </div>
                        </Col>
                    </Row>

                    <Row>
                        <h4 className="pt-2 my-3 fw-bold">{languageUse === 'English' ? 'DISCLAIMER' : 'AVIS DE NON-RESPONSABILITÉ'}</h4>
                        <p>
                            {languageUse === 'English' ? `The report and score calculations provided on this website are based on self-declared data submissions by ${profileData?.company}. The information provided in this report shall not be relied upon as a substitute for professional advice. UNGCNSA and its partner shall not be held liable for any errors, omissions, or inaccuracies in the data provided by ${profileData?.company} or for any actions taken in reliance on such information. ${profileData?.company} is solely responsible for the accuracy and completeness of the information they provide.` : `Les rapports et les calculs de score fournis sur ce site web sont basés sur les données autodéclarées soumises par ${profileData?.company}. Les informations fournies dans ce rapport ne doivent pas être considérées comme un substitut aux conseils professionnels. UNGCNSA et son partenaire ne peuvent être tenus responsables des erreurs, omissions ou inexactitudes dans les données fournies par ${profileData?.company} ou pour toute action prise en se fiant à ces informations. ${profileData?.company} est seul responsable de l'exactitude et de l'exhaustivité des informations qu'il fournit.`}
                        </p>
                        <p className="fs-14">For more information & support, reach out to <a className="text-decoration-none" href="mailto:tech@creativebox.world">tech@creativebox.world</a></p>
                    </Row>
                </Container>
                <ProgressPdfReportPage 
                    ref={componentRef}
                    id={decryptedSubId}
                    pdfContent={pdfContent}
                    data={pdfData}
                    options={pdfOptions}
                    horizontalData={pdfHorizontalData}
                    horoizontalOptions={pdfHorizontalOptions}
                    dimensionData={dimensionData}
                    profileData={profileData}
                    overallVal={overallVal}
                    overallValLebel={overallValLebel}
                    overallColor={overallColor}
                    readiness={readiness}
                    questionAns={questionAns}
                    actionPlan={actionPlan}
                    userSign={userSign}
                    approveInfo={approveInfo}
                    lastSubmission={lastSubmission}
                    scorePercent={scorePercent}
                    uploadedDocInfo={uploadedDocInfo}
                    ccptLevel={ccptLevel}
                />
            </>
            }

            <Footer />
            <SignatureModal show={signatureModalShow} handleClose={handleSignatureModalShow} submissionId={decryptedSubId} setUserSignUploaded={setUserSignUploaded} userSignUploaded={userSignUploaded} />
        </>
    );
};

export default DashboardProgress;
    